/**
 * Вкладка «Сценарии» голосового стенда (`?tab=scenarios`): линии сценариев робота «канал × тип» (голос —
 * звонки, чат — переписка бота) и их версии — что изменилось, когда, по каким замечаниям, разница с прошлой,
 * сколько разговоров прошло на версии. Правка одной линии другие не трогает.
 * У версии три вида текста: разница с прошлой, шаблон (файл `vN.md` как есть, со вставками блоков) и «Что уйдёт
 * модели» — собранный текст (блоки, правила из кода, частые ответы базы) и инструменты. В любом тексте можно
 * выделить кусок и оставить замечание — текстом или голосом; разбирает модель, как замечания к звонкам.
 * `GET /api/voice-lab/scenarios`, текст и разница — `GET …/scenarios/{канал}/{тип}/{версия}`, собранный —
 * `…/assembled`, замечание — `POST …/remarks`. Ссылка на версию: `?tab=scenarios&line=voice/vhod&v=v2`.
 * Прототип `/assets/design/voice-lab-scenarios-concept.html`, спека
 * `docs/specs/2026-11-03-voice-lab-scenarios-ux-design.md`.
 */
import { useQuery } from "@tanstack/react-query";
import { MessageSquarePlus, X } from "lucide-react";
import { useRef, useState } from "react";
import { Link, useSearchParams } from "react-router";

import {
  type LabScenarioLine,
  type LabScenarioVersion,
  labRoomRemarksQuery,
  labScenarioAssembledQuery,
  labScenarioQuery,
  labScenariosQuery,
  scenarioRoom,
} from "@/lib/voiceLab";

import { ToolsTable } from "./VoiceLabPrompt";
import { RemarkChip, RemarkForm } from "./VoiceLabRemarks";

const pill = "rounded-full border border-border px-2.5 py-0.5 text-[12px] text-fg-muted hover:bg-bg-hover hover:text-fg";
const pillOn = "rounded-full border border-accent bg-accent/10 px-2.5 py-0.5 text-[12px] text-fg";
const ЦИТАТА = 600; // столько знаков выделенного уходит в замечание (`remarks.ЦИТАТА`)

/** Строка разницы шаблонов: добавленное зелёным, убранное красным, заголовки блоков — приглушённо. */
function Разница({ diff }: { diff: string }) {
  return (
    <pre className="mt-1 overflow-x-auto whitespace-pre-wrap rounded-[var(--radius-ui)] bg-bg-subtle p-2 text-[11.5px] leading-[1.45]">
      {diff
        .split("\n")
        .filter((с) => !с.startsWith("---") && !с.startsWith("+++"))
        .map((с, i) => (
          <span
            key={i}
            className={`block ${с.startsWith("+") ? "bg-ok/15" : с.startsWith("-") ? "bg-err/15" : с.startsWith("@@") ? "text-fg-muted" : ""}`}
          >
            {с || " "}
          </span>
        ))}
    </pre>
  );
}

/** Выделенный в тексте версии кусок — из любого вида текста внутри `box`. */
function выделение(box: HTMLElement | null): string {
  const sel = window.getSelection();
  if (!box || !sel || sel.isCollapsed || !sel.anchorNode || !box.contains(sel.anchorNode)) return "";
  return sel.toString().trim();
}

type Вид = "diff" | "text" | "assembled" | null;

function Собранный({ line, name }: { line: string; name: string }) {
  const q = useQuery(labScenarioAssembledQuery(line, name));
  if (q.isLoading) return <div className="text-[11.5px] text-fg-muted">Собираю…</div>;
  if (q.isError) return <div className="text-[12px] text-err">Не собралось: {String(q.error)}</div>;
  const d = q.data;
  if (!d) return null;
  const черновик = d.tools.length === 0;
  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-[var(--radius-ui)] bg-bg-subtle px-2.5 py-1.5 text-[12px] text-fg-muted" data-annot="как собран текст">
        {черновик ? (
          <>
            Черновик «Чатов»: модели уходит этот текст с раскрытыми блоками; справочник салона и переписку генератор
            кладёт к нему сам. Инструментов нет — ответ одним JSON.
          </>
        ) : (
          <>
            Собрано как в бою (режим {d.sample.mode}, {d.sample.provider}) на образце:{" "}
            {d.sample.name ? `«${d.sample.name}», ${d.sample.location_name}` : "без имени и салона — как входящий"},
            сегодня {d.sample.today.split("-").reverse().join(".")}. Текст = шаблон версии
            {d.blocks.length > 0 && <> + блоки канала ({d.blocks.join(", ")})</>} + правила из кода (как отвечать на
            вопросы, что говорить перед инструментами) + частые ответы базы знаний на сегодня. Инструменты модель получает
            отдельным списком функций — таблица ниже; search_knowledge — поиск в LightRAG.
          </>
        )}
        <span className="ml-1 font-mono text-[11px]">{d.label}</span>
      </div>
      {!черновик && <ToolsTable d={d} />}
      <div className="flex flex-col gap-1">
        <h3 className="m-0 text-[13px] font-semibold">Текст · {d.text.length.toLocaleString("ru")} знаков</h3>
        <pre className="max-h-[70vh] overflow-auto whitespace-pre-wrap rounded-[var(--radius-ui)] bg-bg-subtle p-2 font-sans text-[12px] leading-relaxed">
          {d.text}
        </pre>
      </div>
    </div>
  );
}

function Версия({
  л,
  в,
  прошлая,
  текущая,
  открыта,
}: {
  л: LabScenarioLine;
  в: LabScenarioVersion;
  прошлая?: string | undefined;
  текущая: boolean;
  открыта: boolean;
}) {
  const [вид, setВид] = useState<Вид>(открыта ? "text" : текущая && прошлая ? "diff" : null);
  const [выделено, setВыделено] = useState("");
  const [форма, setФорма] = useState<string | null>(null); // цитата формы замечания; "" — к версии целиком
  const box = useRef<HTMLDivElement>(null);
  const q = useQuery({ ...labScenarioQuery(л.line, в.name), enabled: вид === "diff" || вид === "text" });
  const room = scenarioRoom(л.line, в.name);
  const замечания = useQuery({
    ...labRoomRemarksQuery(room),
    refetchInterval: (query) =>
      query.state.data?.remarks.some((r) => r.status === "waiting_voice") && !document.hidden ? 5_000 : false,
  });
  const список = замечания.data?.remarks ?? [];
  const показать = (x: Вид) => {
    setВид(вид === x ? null : x);
    setВыделено("");
  };
  return (
    <div
      className={`flex flex-col gap-1 rounded-[var(--radius-ui)] border px-2.5 py-2 ${текущая ? "border-accent" : "border-border"}`}
      data-annot="версия сценария"
    >
      <div className="flex flex-wrap items-center gap-2">
        <b>{в.name}</b>
        <span className="text-[11.5px] text-fg-muted">{в.date || "—"}</span>
        {текущая && <span className="rounded-full border border-ok px-1.5 text-[11px] text-ok">в бою</span>}
        {в.remarks.map((r) =>
          r.room ? (
            <Link
              key={r.id}
              to={`/voice-lab?tab=calls&call=${r.room}&ctab=remarks`}
              className="rounded-full border border-accent px-1.5 text-[11px] text-accent hover:bg-accent/10"
              title="Замечание, по которому сделана версия — открыть разговор"
            >
              замечание #{r.id}
            </Link>
          ) : (
            <span key={r.id} className="rounded-full border border-border px-1.5 text-[11px] text-fg-muted">
              замечание #{r.id}
            </span>
          ),
        )}
        <span className="ml-auto font-mono text-[11.5px] text-fg-muted" title="sha1 шаблона версии и строк в нём">
          {в.hash} · {в.lines} строк
        </span>
      </div>
      <div className="whitespace-pre-wrap">{в.description}</div>
      <div className="flex flex-wrap items-center gap-1.5 text-[12px]">
        {прошлая ? (
          <button type="button" className={вид === "diff" ? pillOn : pill} onClick={() => показать("diff")} aria-pressed={вид === "diff"}>
            Что изменилось против {прошлая}
          </button>
        ) : (
          <span className="text-[11.5px] text-fg-muted">Первая версия линии — сравнивать не с чем.</span>
        )}
        <button
          type="button"
          className={вид === "text" ? pillOn : pill}
          onClick={() => показать("text")}
          aria-pressed={вид === "text"}
          title="Файл версии как есть: <<<блок>>> — вставка общего блока канала, {поле} — подстановка при сборке"
        >
          Шаблон
        </button>
        <button
          type="button"
          className={вид === "assembled" ? pillOn : pill}
          onClick={() => показать("assembled")}
          aria-pressed={вид === "assembled"}
          title="Собранный текст с блоками, правилами из кода и базой знаний и инструменты — то, что получает модель"
        >
          Что уйдёт модели
        </button>
        <button type="button" className={pill} onClick={() => setФорма("")} title="Замечание ко всей версии, без выделения">
          <MessageSquarePlus size={12} className="mr-1 inline" aria-hidden />
          Замечание к версии
        </button>
        <Link
          to={`/voice-lab?tab=calls&version=${encodeURIComponent(`${л.line}/${в.name}`)}`}
          className={pill}
          title="Журнал с отбором по этой версии"
        >
          Разговоры на этой версии · {в.calls_30d} за 30 дней
        </Link>
      </div>
      {форма != null && (
        <div className="flex flex-col gap-1">
          {форма && (
            <div className="max-w-xl border-l-2 border-accent pl-2 text-[12px] text-fg-muted whitespace-pre-wrap">
              «{форма.length > 300 ? `${форма.slice(0, 300)}…` : форма}»
            </div>
          )}
          <RemarkForm
            room={room}
            scenario={{ line: л.line, name: в.name, quote: форма.slice(0, ЦИТАТА) }}
            onClose={() => setФорма(null)}
            onSaved={() => setВыделено("")}
          />
        </div>
      )}
      {список.length > 0 && (
        <div className="flex flex-col gap-1.5" data-annot="замечания к версии">
          {список.map((r) => (
            <RemarkChip key={r.id} r={r} showQuote />
          ))}
        </div>
      )}
      {вид && (
        <div
          ref={box}
          className="relative min-w-0"
          onMouseUp={() => setВыделено(выделение(box.current))}
          onKeyUp={() => setВыделено(выделение(box.current))}
          data-annot="текст версии"
        >
          {вид !== "diff" && !выделено && форма == null && (
            <div className="text-[11.5px] text-fg-muted">Выделите кусок текста — появится кнопка «Замечание к выделенному».</div>
          )}
          {выделено && форма == null && (
            <div className="sticky top-0 z-10 mb-1 flex w-full min-w-0 items-center gap-2 rounded-[var(--radius-ui)] border border-accent bg-bg px-2 py-1 text-[12px] shadow-sm">
              {/* длинное выделение не растягивает экран: строка режется по ширине, в превью — 200 знаков */}
              <span className="min-w-0 flex-1 truncate text-fg-muted" title={выделено.slice(0, ЦИТАТА)}>
                Выделено: «{выделено.replace(/\s+/g, " ").slice(0, 200)}»
              </span>
              <button
                type="button"
                className="shrink-0 rounded-full bg-accent px-3 py-0.5 font-medium text-white"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => setФорма(выделено)}
              >
                Замечание к выделенному
              </button>
              <button
                type="button"
                className="grid size-6 shrink-0 place-items-center rounded-full hover:bg-bg-hover"
                onClick={() => setВыделено("")}
                title="Снять выделение"
              >
                <X size={13} aria-hidden />
              </button>
            </div>
          )}
          {(вид === "diff" || вид === "text") && q.isLoading && <div className="text-[11.5px] text-fg-muted">Загружаю…</div>}
          {(вид === "diff" || вид === "text") && q.isError && (
            <div className="text-[12px] text-err">Не загрузилось: {String(q.error)}</div>
          )}
          {вид === "diff" &&
            q.data &&
            (q.data.diff ? <Разница diff={q.data.diff} /> : <div className="text-fg-muted">Текст не менялся</div>)}
          {вид === "text" && q.data && (
            <pre className="mt-1 max-h-[60vh] overflow-auto whitespace-pre-wrap rounded-[var(--radius-ui)] bg-bg-subtle p-2 text-[11.5px]">
              {q.data.text}
            </pre>
          )}
          {вид === "assembled" && <Собранный line={л.line} name={в.name} />}
        </div>
      )}
    </div>
  );
}

export function VoiceLabScenarios() {
  const q = useQuery(labScenariosQuery());
  const [params, setParams] = useSearchParams();
  const линии = q.data?.lines ?? [];
  const выбрана = params.get("line");
  const л = линии.find((x) => x.line === выбрана) ?? линии[0];
  const версии = л ? [...л.versions].reverse() : []; // новые сверху
  const выбрать = (line: string) =>
    setParams(
      (p) => {
        p.set("line", line);
        p.delete("v");
        return p;
      },
      { replace: true },
    );
  return (
    <section className="flex max-w-6xl flex-col gap-3 text-[12.5px]" data-annot="сценарии робота">
      <p className="m-0 text-fg-muted">
        Тип обращения общий для каналов, у пары «канал × тип» — своя линия версий: правка чата не меняет голос. Новая
        версия появляется после разбора замечания; «в бою» — версия, по которой сейчас идут разговоры. В тексте версии
        можно выделить кусок и оставить замечание текстом или голосом.
      </p>
      {q.isLoading && <div className="text-fg-muted">Загружаю…</div>}
      {q.isError && <div className="text-err">Сценарии не загрузились</div>}
      {л && (
        <div className="grid gap-4 md:grid-cols-[260px_minmax(0,1fr)]">
          <div className="flex flex-col gap-1.5" data-annot="линии сценариев">
            {линии.map((x) => (
              <button
                key={x.line}
                type="button"
                onClick={() => выбрать(x.line)}
                aria-pressed={x.line === л.line}
                className={`flex flex-col gap-0.5 rounded-[var(--radius-ui)] border px-2.5 py-2 text-left hover:bg-bg-hover ${x.line === л.line ? "border-accent bg-accent/10" : "border-border"}`}
              >
                <b>
                  {x.channel_label} · {x.type_label}
                </b>
                <span className="text-[11.5px] text-fg-muted">
                  в бою {x.current} · {x.calls_7d} разговоров за 7 дней
                  {x.remarks_waiting > 0 && (
                    <span className="text-accent"> · {x.remarks_waiting} замечаний ждут</span>
                  )}
                </span>
              </button>
            ))}
            <div className="rounded-[var(--radius-ui)] bg-bg-subtle px-2.5 py-1.5 text-[12px] text-fg-muted">
              Общие куски сценария канала — блоки (тон, запись, правила); их список — у версии. «Что уйдёт модели» —
              текст после сборки и инструменты. Чатовые линии «Прокат» и «Оплата» — черновики «Чатов» по типу
              обращения, проба — команда chat-ai.
            </div>
          </div>
          <div className="flex min-w-0 flex-col gap-2">
            <div className="flex flex-wrap items-baseline gap-2">
              <h2 className="m-0 text-[14px] font-semibold">
                {л.channel_label} · {л.type_label}
              </h2>
              <span className="font-mono text-[11.5px] text-fg-muted">{л.line}</span>
              {л.remarks_waiting > 0 && (
                <Link to="/voice-lab?tab=remarks" className="text-accent hover:underline">
                  Замечания ждут разбора · {л.remarks_waiting}
                </Link>
              )}
            </div>
            {версии.map((в, i) => (
              <Версия
                key={`${л.line}/${в.name}`}
                л={л}
                в={в}
                прошлая={версии[i + 1]?.name}
                текущая={в.name === л.current}
                открыта={params.get("v") === в.name && выбрана === л.line}
              />
            ))}
          </div>
        </div>
      )}
    </section>
  );
}
